import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Info, Minus, Plus } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { StampCard } from '@chillout/ui'
import { images } from '../../../assets'
import { CARD_ICON_COMPONENTS } from '@chillout/ui'
import type { LoyaltyConfig } from './useLoyaltyConfig'

interface CustomerPreviewSectionProps {
  config: LoyaltyConfig
  stampCount: number
  onStampCountChange: (count: number) => void
  variant: 'sidebar' | 'inline'
}

export function CustomerPreviewSection({ config, stampCount, onStampCountChange, variant }: CustomerPreviewSectionProps) {
  const { t } = useTranslation()
  const stampTarget = Math.max(config.stampTarget, 0)
  const current = Math.min(Math.max(stampCount, 0), stampTarget)
  const remaining = stampTarget - current
  const allowSideBySide = variant === 'inline'

  function iconForPosition(position: number) {
    if (position === stampTarget) return CARD_ICON_COMPONENTS[config.completionCardIcon]
    const milestone = config.milestones.find((m) => m.stampPosition === position)
    return milestone ? CARD_ICON_COMPONENTS[milestone.cardIcon] : undefined
  }

  return (
    <View className="gap-5 rounded-xl border border-border bg-canvas-card p-6">
      <View>
        <Text className="text-lg font-semibold text-primary">{t('loyalty.customerPreview.sectionTitle')}</Text>
        <Text className="mt-1 text-sm text-secondary">{t('loyalty.customerPreview.subtitle')}</Text>
      </View>

      <View className={`flex-col gap-6 ${allowSideBySide ? 'lg:flex-row lg:items-stretch' : ''}`}>
        <View className={`w-full max-w-[400px] gap-5 ${allowSideBySide ? 'lg:shrink-0' : ''}`}>
          <StampCard
            current={current}
            total={stampTarget}
            logoSource={images.logoDark}
            logoAccessibilityLabel={t('loyalty.customerPreview.logoAccessibilityLabel')}
            countText={t('loyalty.customerPreview.count', { current, total: stampTarget })}
            subtitleText={
              remaining > 0
                ? t('loyalty.customerPreview.subtitleRemaining', { remaining })
                : t('loyalty.customerPreview.subtitleComplete')
            }
            accessibilityLabel={t('loyalty.customerPreview.cardAccessibilityLabel', { current, total: stampTarget })}
            iconForPosition={iconForPosition}
          />

          <View className="flex-row items-start gap-2">
            <Info size={14} color={colors.text.muted} strokeWidth={2} />
            <Text className="flex-1 text-xs text-muted">{t('loyalty.customerPreview.layoutDisclaimer')}</Text>
          </View>
        </View>

        <View className={`h-px w-full bg-border ${allowSideBySide ? 'lg:h-auto lg:w-px lg:self-stretch' : ''}`} />

        <View className="flex-1 gap-4">
          <View>
            <Text className="text-sm font-semibold text-primary">{t('loyalty.customerPreview.previewProgress')}</Text>
            <Text className="mt-1 text-sm text-secondary">{t('loyalty.customerPreview.previewProgressDescription')}</Text>
          </View>

          <View className="flex-row items-center justify-end gap-3">
            <Pressable
              onPress={() => onStampCountChange(Math.max(0, current - 1))}
              disabled={current <= 0}
              accessibilityRole="button"
              accessibilityLabel={t('loyalty.customerPreview.decrementAccessibilityLabel')}
              accessibilityState={{ disabled: current <= 0 }}
              className={`h-10 w-10 items-center justify-center rounded-md border border-border ${current <= 0 ? 'opacity-40' : ''}`}
            >
              <Minus size={16} color={colors.text.secondary} strokeWidth={2} />
            </Pressable>

            <Text className="w-8 text-center text-base font-semibold text-primary">{current}</Text>

            <Pressable
              onPress={() => onStampCountChange(Math.min(stampTarget, current + 1))}
              disabled={current >= stampTarget}
              accessibilityRole="button"
              accessibilityLabel={t('loyalty.customerPreview.incrementAccessibilityLabel')}
              accessibilityState={{ disabled: current >= stampTarget }}
              className={`h-10 w-10 items-center justify-center rounded-md border border-border ${current >= stampTarget ? 'opacity-40' : ''}`}
            >
              <Plus size={16} color={colors.text.secondary} strokeWidth={2} />
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  )
}